import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import ShadowWrapper from '../ShadowWrapper/ShadowWrapper.jsx';
import {
  shouldShowMarketingModal,
  hasUserSeenMarketingModal,
  markMarketingModalCompleted
} from '../../lib/utm-utils.js';
import styles from './PopupModal.styles.css';

const PopupModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    zip: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    const shouldShow = shouldShowMarketingModal() && !hasUserSeenMarketingModal();
    if (shouldShow) {
      setIsOpen(true);
      document.body.style.overflow = 'hidden';
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';

      const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
          handleClose();
        }
      };

      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = '';
      };
    } else {
      document.body.style.overflow = '';
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);
    document.body.style.overflow = '';
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.zip) {
      return;
    }

    setIsSubmitting(true);

    try {
      const form = new FormData();
      form.append('form-name', 'marketing-campaign');
      form.append('name', formData.name);
      form.append('email', formData.email);
      form.append('zip', formData.zip);

      console.log('Submitting form data:', Object.fromEntries(form));

      // Netlify forms must be submitted to the root path
      const response = await fetch('/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams(form).toString()
      });

      console.log('Form submission response:', response.status, response.statusText);

      if (response.ok) {
        setSubmitSuccess(true);
        markMarketingModalCompleted();
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        throw new Error(`Form submission failed: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert(`Form submission failed: ${error.message}`);
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ShadowWrapper styles={styles}>
      <div className="overlay" onClick={handleOverlayClick}>
        <div className="modal">
          <button className="close-button" onClick={handleClose} aria-label="Close modal">
            ×
          </button>

          {submitSuccess ? (
            <div className="success-content">
              <h2>Thank you!</h2>
              <p>We'll be in touch soon with more information.</p>
            </div>
          ) : (
            <div className="modal-content">
              <h2>What if Crossroads came to your city?</h2>
              <p>Drop your info to know more:</p>

              <form
                name="marketing-campaign"
                method="POST"
                data-netlify="true"
                onSubmit={handleSubmit}
                className="form"
              >
                <input type="hidden" name="form-name" value="marketing-campaign" />

                <div className="form-group">
                  <label htmlFor="name">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    onInput={handleInputChange}
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onInput={handleInputChange}
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="zip">Zip</label>
                  <input
                    type="text"
                    id="zip"
                    name="zip"
                    value={formData.zip}
                    onChange={handleInputChange}
                    onInput={handleInputChange}
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <button
                  type="submit"
                  className="submit-button"
                  disabled={isSubmitting || !formData.name || !formData.email || !formData.zip}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </ShadowWrapper>
  );
};

export default PopupModal;
